import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { api, getBootVersion, previewToken } from "../api.js";
import { useT } from "../lib/i18n.jsx";

// Venue versions (item 0375). Mounted next to the viewer (App.jsx), not inside
// it: it only watches.
//
// Screens (?kiosk=) run for days without a reload, so they poll the tiny
// /version endpoint and reload when a new version has been published -- but
// only after QUIET_MS without a touch, so nobody standing at the totem loses
// their route mid-sentence. Phones are not polled: they get the new version on
// their next visit. A preview is a fixed version and is never polled either.
const POLL_MS = 120_000;
const QUIET_MS = 60_000;
const TICK_MS = 5_000;

export default function PublishWatcher() {
  const { slug } = useParams();
  const { t } = useT();
  const preview = previewToken();
  const kiosk = new URLSearchParams(window.location.search).get("kiosk");

  useEffect(() => {
    if (preview || !kiosk || !slug) return undefined;
    let lastTouch = Date.now();
    let pending = false;
    const touched = () => {
      lastTouch = Date.now();
    };
    const events = ["pointerdown", "touchstart", "keydown", "wheel"];
    events.forEach((name) => window.addEventListener(name, touched, { passive: true }));

    // Ask at most every POLL_MS (jittered, so a venue's screens do not all ask
    // in the same second); once a new version is known, check every TICK_MS
    // whether the screen has been quiet long enough to reload.
    let nextPoll = Date.now() + 15_000 + Math.random() * 15_000;
    const tick = async () => {
      if (!pending && Date.now() >= nextPoll) {
        nextPoll = Date.now() + POLL_MS + Math.random() * 10_000;
        const live = await api.version(slug).catch(() => null);
        const booted = getBootVersion();
        // No answer (offline, 402, 5xx) is not "changed": keep showing what we have.
        if (live?.id && booted?.id && live.id !== booted.id) pending = true;
      }
      if (pending && Date.now() - lastTouch >= QUIET_MS) window.location.reload();
    };
    const timer = setInterval(tick, TICK_MS);
    return () => {
      clearInterval(timer);
      events.forEach((name) => window.removeEventListener(name, touched));
    };
  }, [preview, kiosk, slug]);

  if (!preview) return null;
  return (
    <div
      className="pointer-events-none fixed bottom-16 left-1/2 z-30 -translate-x-1/2 sm:bottom-3 rounded-full bg-amber-500/95 px-3 py-1.5 text-xs font-semibold text-white shadow-lg"
      role="status"
    >
      {t("preview_ribbon")}
    </div>
  );
}
